'use client';

import { useAuth } from '@/backend/context/auth-management';
import { useClients } from '@/backend/firebase/hooks/useClients';
import { InternationalPhoneInput } from '@/components/phone-input';
import { FormSheetLayout } from '@/components/form-sheet-layout';
import { Input } from '@/components/ui/input';
import type { ClientWithId } from '@/shared/schema';
import { formatPhoneNumber } from '@/shared/lib/client-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Check, ArrowRight, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const clientSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  phone: z.string().optional().or(z.literal('')),
});

type ClientFormData = z.infer<typeof clientSchema>;

interface CreateClientModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (client: ClientWithId) => void;
  defaultValues?: { name?: string; phone?: string };
  editingClientId?: string;
  mode?: 'create' | 'merge' | 'edit';
  recordType?: 'lead' | 'client';
}

export function CreateClientModal({
  open,
  onOpenChange,
  onSuccess,
  defaultValues,
  editingClientId,
  mode = editingClientId ? 'merge' : 'create',
  recordType = 'client',
}: CreateClientModalProps) {
  const ownerId = useAuth((s) => s.profile?.id);
  const createMutation = useClients.create();
  const updateMutation = useClients.update();

  const isMerge = mode === 'merge';
  const isEdit = mode === 'edit';

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: defaultValues || { name: '', phone: '' },
  });

  const phoneValue = watch('phone');
  const nameValue = watch('name');
  const [discoveredClient, setDiscoveredClient] = useState<
    ClientWithId | null | undefined
  >(undefined);

  useEffect(() => {
    if (discoveredClient) setDiscoveredClient(null);
  }, [phoneValue]);

  const formattedSearchPhone = phoneValue ? formatPhoneNumber(phoneValue) : '';
  const { refetch, isFetching: checkingPhone } = useClients.getByPhoneNumber(
    formattedSearchPhone,
    { enabled: false },
  );

  useEffect(() => {
    if (open) {
      reset({
        name: defaultValues?.name || '',
        phone: defaultValues?.phone || '',
      });
    }
  }, [open, defaultValues, reset]);

  useEffect(() => {
    const checkDuplicate = async () => {
      if (formattedSearchPhone.length >= 9) {
        const { data: existingClients } = await refetch();
        const match = existingClients?.[0];
        if (match && match.id !== editingClientId) {
          setDiscoveredClient(match);
        } else {
          setDiscoveredClient(null);
        }
      } else {
        setDiscoveredClient(null);
      }
    };
    const timer = setTimeout(checkDuplicate, 500);
    return () => clearTimeout(timer);
  }, [formattedSearchPhone, refetch, editingClientId]);

  const handleUpdateNameAndUse = async () => {
    if (!discoveredClient || !ownerId) return;
    try {
      await updateMutation.mutateAsync({
        clientId: discoveredClient.id,
        data: { name: nameValue },
      });
      toast.success('Nombre actualizado con éxito');
      onOpenChange(false);
      if (onSuccess) onSuccess({ ...discoveredClient, name: nameValue });
    } catch (error) {
      toast.error('No se pudo actualizar el nombre');
    }
  };

  const onSubmit = async (data: ClientFormData) => {
    if (!ownerId) return;
    try {
      let currentMatch = discoveredClient;
      if (data.phone && data.phone.length >= 9) {
        const { data: freshData } = await refetch();
        currentMatch = freshData?.[0] || null;
      }
      
      if (currentMatch && currentMatch.id !== editingClientId) {
        setDiscoveredClient(currentMatch);
        return;
      }
      let finalClient: ClientWithId;
      if (editingClientId) {
        await updateMutation.mutateAsync({
          clientId: editingClientId,
          data: { name: data.name, phone: data.phone },
        });
        finalClient = { id: editingClientId, ...data } as any;
        toast.success('¡Listo! Datos guardados');
      } else {
        finalClient = await createMutation.mutateAsync({
          name: data.name,
          phone: data.phone || '',
          status: recordType === 'lead' ? 'CREATED' : 'SOLD',
          source: 'manual',
        });
        toast.success(
          recordType === 'lead'
            ? 'Captación registrada'
            : '¡Bienvenido! Cliente registrado',
        );
      }
      onOpenChange(false);
      if (onSuccess) onSuccess(finalClient);
    } catch (error) {
      toast.error('Algo salió mal, intenta de nuevo');
    }
  };

  const handleKeepExisting = () => {
    if (discoveredClient) {
      onOpenChange(false);
      if (onSuccess) onSuccess(discoveredClient);
    }
  };

  return (
    <FormSheetLayout
      isOpen={open}
      onOpenChange={onOpenChange}
      heroTitle={discoveredClient ? "¡Lo encontramos!" : (isEdit ? "Editar Cliente" : isMerge ? "Vincular Datos" : recordType === 'lead' ? "Nueva Captación" : "Nuevo Cliente")}
      primaryAction={{
        label: discoveredClient 
          ? `MANTENER COMO "${discoveredClient.name.split(' ')[0]}"` 
          : (isEdit ? "Guardar Cambios" : recordType === 'lead' ? "Guardar Lead" : "Confirmar Cliente"),
        onClick: discoveredClient ? handleKeepExisting : handleSubmit((data) => onSubmit(data)),
        isLoading: createMutation.isPending || updateMutation.isPending || checkingPhone,
      }}
    >
      <div className="space-y-8 pb-10">
        {discoveredClient && (
          <div className="animate-in fade-in zoom-in-95 duration-300 flex flex-col gap-4 p-6 rounded-3xl bg-amber-50 border-2 border-amber-200 mb-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
                <AlertCircle className="h-6 w-6 text-amber-700" />
              </div>
              <div className="flex-1">
                 <p className="text-[10px] font-black uppercase text-amber-600 tracking-widest leading-none mb-1">Número en uso</p>
                 <p className="text-sm font-bold text-amber-950 leading-tight">
                  Ya existe alguien con este número: <span className="uppercase underline decoration-2 underline-offset-2">{discoveredClient.name}</span>.
                 </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              <button 
                onClick={handleUpdateNameAndUse}
                disabled={updateMutation.isPending}
                className="group flex items-center justify-between h-14 px-5 rounded-2xl bg-zinc-900 text-white active:scale-95 transition-all shadow-md disabled:opacity-50"
              >
                <div className="text-left">
                  <p className="text-[9px] font-black uppercase text-zinc-400 leading-none">Opción A</p>
                  <p className="text-[12px] font-black uppercase italic">Actualizar a "{nameValue}"</p>
                </div>
                {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-5 w-5 text-zinc-400" />}
              </button>

              <button 
                onClick={handleKeepExisting}
                className="group flex items-center justify-between h-14 px-5 rounded-2xl bg-white border-2 border-zinc-100 text-zinc-900 active:scale-95 transition-all shadow-sm"
              >
                <div className="text-left">
                  <p className="text-[9px] font-black uppercase text-zinc-400 leading-none">Opción B</p>
                  <p className="text-[12px] font-black uppercase italic">Usar como "{discoveredClient.name}"</p>
                </div>
                <Check className="h-5 w-5 text-zinc-300" />
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <label className="ml-2 text-[10px] font-black text-zinc-400 uppercase tracking-widest">Nombre Completo</label>
          <Input
            {...register('name')}
            placeholder="Ej. Juan Pérez"
            enterKeyHint="next"
            className="h-16 rounded-2xl border-2 border-zinc-100 bg-zinc-50 px-6 text-lg font-black italic uppercase tracking-tighter focus-visible:ring-zinc-900 text-base shadow-inner"
          />
          {errors.name && <p className="ml-2 text-[10px] font-bold text-rose-500">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <label className="ml-2 text-[10px] font-black text-zinc-400 uppercase tracking-widest">Número WhatsApp</label>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <InternationalPhoneInput
                value={field.value!}
                onChange={field.onChange}
                error={!!errors.phone}
              />
            )}
          />
          {errors.phone && <p className="ml-2 text-[10px] font-bold text-rose-500">{errors.phone.message}</p>}
        </div>
      </div>
    </FormSheetLayout>
  );
}
